const ACCOUNT_ICON_SPRITE_PATH = '/assets/icons/quaom-account-icons.svg';
const ACCOUNT_GENERATED_PASSWORD_LENGTH = 18;
const ACCOUNT_PASSWORD_LOWER = 'abcdefghijkmnopqrstuvwxyz';
const ACCOUNT_PASSWORD_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const ACCOUNT_PASSWORD_DIGITS = '23456789';
const ACCOUNT_PASSWORD_ALL = `${ACCOUNT_PASSWORD_LOWER}${ACCOUNT_PASSWORD_UPPER}${ACCOUNT_PASSWORD_DIGITS}`;

export type AccountAuthFieldOptions = {
  value?: string;
  autocomplete?: string;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  required?: boolean;
  spellcheck?: boolean;
  autocapitalize?: string;
  hint?: string;
  placeholder?: string;
  labelAction?: string;
  scope?: string;
  fieldClass?: string;
  inputClass?: string;
  labelClass?: string;
  hintClass?: string;
  dataAttributes?: Record<string, string | number | boolean | null | undefined>;
  inputDataAttributes?: Record<string, string | number | boolean | null | undefined>;
  secretToggle?: boolean;
  secretShowLabel?: string;
  secretHideLabel?: string;
};

export type AccountAuthCheckboxOptions = {
  checked?: boolean;
  fieldClass?: string;
  labelClass?: string;
  scope?: string;
  dataAttributes?: Record<string, string | number | boolean | null | undefined>;
};

export type AccountRecoverySecretBoxOptions = {
  label: string;
  secret: string;
  warning: string;
  copyLabel: string;
  downloadLabel: string;
  copiedLabel: string;
  downloadedLabel: string;
  pseudonym?: string;
  pseudonymLabel: string;
  recoveryKeyLabel: string;
  createdLabel: string;
  fileWarning: string;
  filename?: string;
  primaryActionLabel?: string;
  primaryActionHref?: string;
};

export function renderAccountAuthField(
  name: string,
  label: string,
  type: string,
  options: AccountAuthFieldOptions = {},
): string {
  const scope = safeScope(options.scope || `auth-${name}`);
  const hintId = `${scope}-hint`;
  const hasHint = Boolean(options.hint);
  const inputDataAttrs = {
    ...(options.inputDataAttributes || {}),
    ...(options.secretToggle ? { 'auth-secret-input': true } : {}),
  };
  const dataAttrs = dataAttributeString({
    'auth-field': true,
    'auth-field-name': name,
    ...(options.secretToggle ? { 'auth-field-secret': true } : {}),
    ...(options.dataAttributes || {}),
  });
  const inputClass = composeClass(
    options.inputClass || '',
    options.secretToggle ? 'qe-auth-field__secret-input' : '',
  );
  const inputId = `${scope}-input`;
  const attrs = [
    `id="${escapeAttr(inputId)}"`,
    `name="${escapeAttr(name)}"`,
    `type="${escapeAttr(type)}"`,
    `value="${escapeAttr(options.value || '')}"`,
    options.autocomplete ? `autocomplete="${escapeAttr(options.autocomplete)}"` : '',
    Number.isFinite(options.minLength) ? `minlength="${escapeAttr(options.minLength)}"` : '',
    Number.isFinite(options.maxLength) ? `maxlength="${escapeAttr(options.maxLength)}"` : '',
    options.pattern ? `pattern="${escapeAttr(options.pattern)}"` : '',
    options.required ? 'required' : '',
    options.spellcheck === false ? 'spellcheck="false"' : '',
    options.autocapitalize ? `autocapitalize="${escapeAttr(options.autocapitalize)}"` : '',
    options.placeholder ? `placeholder="${escapeAttr(options.placeholder)}"` : '',
    hasHint ? `aria-describedby="${escapeAttr(hintId)}"` : '',
    inputClass ? `class="${escapeAttr(inputClass)}"` : '',
    dataAttributeString(inputDataAttrs),
  ]
    .filter(Boolean)
    .join(' ');
  const input = `<input ${attrs}>`;
  return [
    `<div class="${escapeAttr(composeClass('qe-auth-field', options.fieldClass))}" ${dataAttrs}>`,
    `<div class="qe-auth-field__label-row"><label for="${escapeAttr(inputId)}" class="${escapeAttr(composeClass('qe-auth-field__label', options.labelClass))}">${escapeHtml(label)}</label>${options.labelAction || ''}</div>`,
    options.secretToggle ? renderSecretControl(input, options) : input,
    hasHint
      ? `<small class="${escapeAttr(composeClass('qe-auth-field__hint', options.hintClass))}" id="${escapeAttr(hintId)}" data-auth-rules>${escapeHtml(options.hint)}</small>`
      : '',
    '</div>',
  ].join('');
}

export function renderAccountAuthCheckbox(
  name: string,
  label: string,
  options: AccountAuthCheckboxOptions = {},
): string {
  const dataAttrs = dataAttributeString({
    'auth-field': true,
    'auth-field-name': name,
    'auth-field-kind': 'checkbox',
    ...(options.dataAttributes || {}),
  });
  return [
    `<label class="${escapeAttr(composeClass('qe-auth-field qe-auth-field--inline', options.fieldClass))}" ${dataAttrs}>`,
    `<input name="${escapeAttr(name)}" type="checkbox"${options.checked ? ' checked' : ''}>`,
    `<span class="${escapeAttr(composeClass('qe-auth-field__label', options.labelClass))}">${escapeHtml(label)}</span>`,
    '</label>',
  ].join('');
}

export function renderAccountPasswordGenerator(label: string, targetName = 'password'): string {
  return `<button class="qe-auth-password-generate" type="button" data-auth-password-generate data-auth-password-target="${escapeAttr(targetName)}">${escapeHtml(label)}</button>`;
}

export function renderAccountRecoverySecretBox(options: AccountRecoverySecretBoxOptions): string {
  return [
    `<div class="account-secret" data-account-secret-box data-account-secret-filename="${escapeAttr(options.filename || 'quaom-recovery-key.txt')}" data-account-secret-pseudonym="${escapeAttr(options.pseudonym || '')}" data-account-secret-pseudonym-label="${escapeAttr(options.pseudonymLabel)}" data-account-secret-recovery-label="${escapeAttr(options.recoveryKeyLabel)}" data-account-secret-created-label="${escapeAttr(options.createdLabel)}" data-account-secret-file-warning="${escapeAttr(options.fileWarning)}" data-account-secret-copied-label="${escapeAttr(options.copiedLabel)}" data-account-secret-downloaded-label="${escapeAttr(options.downloadedLabel)}">`,
    `<div class="account-secret__header"><strong>${escapeHtml(options.label)}</strong><div class="account-secret__actions">`,
    `<button class="account-icon-button account-secret__action" type="button" data-account-copy-secret aria-label="${escapeAttr(options.copyLabel)}" title="${escapeAttr(options.copyLabel)}">${accountAuthIcon('copy')}</button>`,
    `<button class="account-icon-button account-secret__action" type="button" data-account-download-secret aria-label="${escapeAttr(options.downloadLabel)}" title="${escapeAttr(options.downloadLabel)}">${accountAuthIcon('download')}</button>`,
    '</div></div>',
    `<code data-account-secret-value>${escapeHtml(options.secret)}</code>`,
    `<p class="account-secret__warning">${escapeHtml(options.warning)}</p>`,
    '<p class="account-secret__status" data-account-secret-status aria-live="polite"></p>',
    options.primaryActionLabel && options.primaryActionHref
      ? `<div class="account-secret__footer"><a class="account-button account-button--secondary account-secret__open" href="${escapeAttr(options.primaryActionHref)}">${escapeHtml(options.primaryActionLabel)}</a></div>`
      : '',
    '</div>',
  ].join('');
}

export function bindAccountAuthSecretToggles(root: ParentNode = document): void {
  root.querySelectorAll<HTMLButtonElement>('[data-auth-secret-toggle]').forEach((button) => {
    if (button.dataset.authSecretToggleBound === 'true') return;
    button.dataset.authSecretToggleBound = 'true';
    const control = button.closest<HTMLElement>('[data-auth-secret-control]');
    const input = control?.querySelector<HTMLInputElement>('[data-auth-secret-input]');
    const icon = button.querySelector<SVGUseElement>('use');
    if (!input || !icon) return;
    const showLabel = button.dataset.authSecretShowLabel || button.getAttribute('aria-label') || '';
    const hideLabel = button.dataset.authSecretHideLabel || showLabel;
    const sync = (visible: boolean) => {
      input.type = visible ? 'text' : 'password';
      button.setAttribute('aria-pressed', visible ? 'true' : 'false');
      button.setAttribute('aria-label', visible ? hideLabel : showLabel);
      button.setAttribute('title', visible ? hideLabel : showLabel);
      const iconHref = `${ACCOUNT_ICON_SPRITE_PATH}#qe-icon-${visible ? 'eye-off' : 'eye'}`;
      icon.setAttribute('href', iconHref);
      icon.setAttributeNS('http://www.w3.org/1999/xlink', 'href', iconHref);
    };
    button.addEventListener('click', () => sync(input.type !== 'text'));
    sync(input.type === 'text');
  });
}

export function bindAccountPasswordGenerators(root: ParentNode = document): void {
  root.querySelectorAll<HTMLButtonElement>('[data-auth-password-generate]').forEach((button) => {
    if (button.dataset.authPasswordGenerateBound === 'true') return;
    button.dataset.authPasswordGenerateBound = 'true';
    button.addEventListener('click', () => {
      const targetName = button.dataset.authPasswordTarget || 'password';
      const form = button.closest('form');
      const input = form?.querySelector<HTMLInputElement>(`input[name="${cssEscape(targetName)}"]`);
      if (!input) return;
      input.value = generateAccountPassword();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.focus();
    });
  });
}

export function bindAccountRecoverySecretActions(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-account-secret-box]').forEach((box) => {
    if (box.dataset.accountSecretActionsBound === 'true') return;
    box.dataset.accountSecretActionsBound = 'true';
    const value = box.querySelector<HTMLElement>('[data-account-secret-value]')?.textContent || '';
    const status = box.querySelector<HTMLElement>('[data-account-secret-status]');
    const setStatus = (message: string) => {
      if (!status) return;
      status.textContent = message;
      window.setTimeout(() => {
        if (status.textContent === message) status.textContent = '';
      }, 1600);
    };
    box
      .querySelector<HTMLButtonElement>('[data-account-copy-secret]')
      ?.addEventListener('click', async () => {
        if (!value) return;
        try {
          await navigator.clipboard?.writeText(value);
          setStatus(box.dataset.accountSecretCopiedLabel || 'Copied');
        } catch {
          setStatus('');
        }
      });
    box
      .querySelector<HTMLButtonElement>('[data-account-download-secret]')
      ?.addEventListener('click', () => {
        if (!value) return;
        const text = buildRecoveryKeyFile(box, value);
        downloadTextFile(box.dataset.accountSecretFilename || 'quaom-recovery-key.txt', text);
        setStatus(box.dataset.accountSecretDownloadedLabel || 'Downloaded');
      });
  });
}

export function generateAccountPassword(): string {
  const chars = [
    randomChar(ACCOUNT_PASSWORD_LOWER),
    randomChar(ACCOUNT_PASSWORD_UPPER),
    randomChar(ACCOUNT_PASSWORD_DIGITS),
  ];
  while (chars.length < ACCOUNT_GENERATED_PASSWORD_LENGTH)
    chars.push(randomChar(ACCOUNT_PASSWORD_ALL));
  return shuffle(chars).join('');
}

function renderSecretControl(input: string, options: AccountAuthFieldOptions): string {
  const showLabel = options.secretShowLabel || 'Show';
  const hideLabel = options.secretHideLabel || 'Hide';
  return [
    '<span class="qe-auth-field__control" data-auth-secret-control>',
    input,
    `<button class="qe-auth-field__secret-toggle" type="button" data-auth-secret-toggle data-auth-secret-show-label="${escapeAttr(showLabel)}" data-auth-secret-hide-label="${escapeAttr(hideLabel)}" aria-label="${escapeAttr(showLabel)}" title="${escapeAttr(showLabel)}" aria-pressed="false">${accountAuthIcon('eye', 'qe-auth-field__secret-icon')}</button>`,
    '</span>',
  ].join('');
}

function buildRecoveryKeyFile(box: HTMLElement, recoveryKey: string): string {
  const lines = ['Quaom', ''];
  const pseudonym = box.dataset.accountSecretPseudonym || '';
  const pseudonymLabel = box.dataset.accountSecretPseudonymLabel || 'Pseudonym';
  const recoveryLabel = box.dataset.accountSecretRecoveryLabel || 'Recovery key';
  const createdLabel = box.dataset.accountSecretCreatedLabel || 'Created';
  const warning = box.dataset.accountSecretFileWarning || '';
  if (pseudonym) lines.push(`${pseudonymLabel}: ${pseudonym}`);
  lines.push(`${recoveryLabel}: ${recoveryKey}`);
  lines.push(
    `${createdLabel}: ${new Date().toLocaleDateString(document.documentElement.lang || undefined)}`,
  );
  if (warning) lines.push('', warning);
  return `${lines.join('\n')}\n`;
}

function downloadTextFile(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function randomChar(chars: string): string {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return chars[values[0] % chars.length];
}

function shuffle(chars: string[]): string[] {
  const next = [...chars];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const random = new Uint32Array(1);
    crypto.getRandomValues(random);
    const swap = random[0] % (index + 1);
    [next[index], next[swap]] = [next[swap], next[index]];
  }
  return next;
}

function accountAuthIcon(name: string, className = 'account-icon'): string {
  const safeName = /^[a-z0-9-]+$/.test(name) ? name : 'circle-x';
  return `<svg class="${escapeAttr(className)}" width="18" height="18" aria-hidden="true" focusable="false"><use href="${ACCOUNT_ICON_SPRITE_PATH}#qe-icon-${safeName}"></use></svg>`;
}

function composeClass(base: string, extra?: string): string {
  return [base, extra || ''].join(' ').trim().replace(/\s+/g, ' ');
}

function dataAttributeString(
  values: Record<string, string | number | boolean | null | undefined>,
): string {
  return Object.entries(values)
    .filter(([, value]) => value !== false && value !== null && value !== undefined)
    .map(([key, value]) =>
      value === true ? `data-${escapeAttr(key)}` : `data-${escapeAttr(key)}="${escapeAttr(value)}"`,
    )
    .join(' ');
}

function safeScope(value: string): string {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || 'auth-field';
}

function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(value: unknown): string {
  return escapeHtml(value).replace(/`/g, '&#96;');
}
